import React, { useState } from 'react';
import type { SetupMatrixResponse } from '../../services/api';
import { fmtSignedPct } from '../../utils/screenerCard';

/**
 * Screener setup matrix (Oct 2026 redesign).
 *
 * Grid = volatility (rows) × 12-week trend (columns); the range dimension is a pill
 * selector that opens on today's range. Layout chosen from the 489-stock history:
 * within behavioral clusters, volatility × trend keeps more of the setup signal than
 * trend × range in 9 of 12 clusters (the pooled figure pointed the other way only
 * because of three clusters).
 *
 * Cells are colored against the STOCK'S OWN average, so green means "better than
 * usual for this stock", not merely positive. Cells under the minimum history say so instead
 * of disappearing. Summary figures reuse the card's 3-column system (sc2-grid3).
 *
 * Gold phase 2b: every figure (cell result, share of days, frequency word, tone vs
 * usual) comes from GET /stocks/{symbol}/setup-matrix; this component only lays
 * them out. The matrix is the stock's CALL history (no put matrix yet).
 */

const HV = [
  { id: 'LO', label: 'Calm' },
  { id: 'MED', label: 'Moderate' },
  { id: 'HI', label: 'Volatile' },
];
const SLOPE = [
  { id: 'DOWN', label: 'Falling' },
  { id: 'FLAT', label: 'Flat' },
  { id: 'UP', label: 'Rising' },
];
const RANGE = [
  { id: 'TIGHT', label: 'Tight range' },
  { id: 'NORMAL', label: 'Normal range' },
  { id: 'EXPANDING', label: 'Widening range' },
];

interface ScreenerSetupMatrixProps {
  symbol: string;
  matrix: SetupMatrixResponse;
  /** The lens: a put lens still shows the call history (no put matrix yet). */
  contract: 'call' | 'put';
}

const ScreenerSetupMatrix: React.FC<ScreenerSetupMatrixProps> = ({ symbol, matrix, contract }) => {
  const { cells, baseline_pnl: baselinePct, current_cell: currentCell } = matrix;
  const minN = matrix.min_n;
  const [curHv, curSlope, curRange] = currentCell ? currentCell.split('_') : [null, null, null];
  const [range, setRange] = useState<string>(curRange && RANGE.some((r) => r.id === curRange) ? curRange : 'NORMAL');

  const cellAt = (hv: string, slope: string, rg: string) => cells[`${hv}_${slope}_${rg}`] ?? null;
  const today = currentCell ? cells[currentCell] ?? null : null;
  const todayPct = today?.pnl_pct ?? null;

  let note: React.ReactNode = null;
  if (currentCell && todayPct == null) {
    note = <>{symbol} has spent too little time in today's setup to measure it. Nearby cells show how it did in similar conditions.</>;
  } else if (matrix.current_frequency === 'rare') {
    note = <>Today's setup is rare for {symbol}, so its average rests on limited history.</>;
  }

  return (
    <div className="smx">
      <div className="sc2-grid3 smx-sum">
        <div className="sc2-cell">
          <span className="sc2-lab">Today's setup</span>
          <span className={`sc2-val ${todayPct != null ? (todayPct >= 0 ? 'sc2-pos' : 'sc2-neg') : ''}`}>
            {todayPct != null ? fmtSignedPct(todayPct) : '—'}
          </span>
        </div>
        <div className="sc2-cell">
          <span className="sc2-lab">{symbol} usual</span>
          <span className={`sc2-val ${baselinePct != null ? (baselinePct >= 0 ? 'sc2-pos' : 'sc2-neg') : ''}`}>
            {baselinePct != null ? fmtSignedPct(baselinePct) : '—'}
          </span>
        </div>
        <div className="sc2-cell">
          <span className="sc2-lab">How often</span>
          <span className="sc2-val">
            {matrix.current_share != null ? <>{`${Math.round(matrix.current_share)}%`}<span className="sc2-unit"> · {matrix.current_frequency}</span></> : '—'}
          </span>
        </div>
      </div>
      <p className="smx-lede">
        Average 35-day call on {symbol}, by setup{contract === 'put' ? ' (call history: no put history yet)' : ''}.{note && <> {note}</>}
      </p>

      <div className="smx-seg" role="radiogroup" aria-label="Trading range">
        {RANGE.map((r) => (
          <button
            key={r.id}
            type="button"
            role="radio"
            aria-checked={range === r.id}
            className={range === r.id ? 'is-on' : ''}
            onClick={() => setRange(r.id)}
            title={r.id === curRange ? "Today's range" : undefined}
          >
            {r.label}
            {r.id === curRange && <span className="smx-dot" aria-label="today" />}
          </button>
        ))}
      </div>

      <table className="smx-grid">
        <caption className="sr-only">
          Average 35-day call result by volatility (rows) and 12-week trend (columns), {RANGE.find((r) => r.id === range)?.label.toLowerCase()}
        </caption>
        <thead>
          <tr>
            <th scope="col"><span className="sr-only">Volatility</span></th>
            {SLOPE.map((s) => (
              <th key={s.id} scope="col">{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HV.map((h) => (
            <tr key={h.id}>
              <th scope="row">{h.label}</th>
              {SLOPE.map((s) => {
                const c = cellAt(h.id, s.id, range);
                const isToday = h.id === curHv && s.id === curSlope && range === curRange;
                const value = c?.pnl_pct ?? null;
                const tone = value != null && c?.tone ? c.tone.replace('_', '-') : 'few';
                return (
                  <td key={s.id} className={`smx-cell is-${tone} ${isToday ? 'is-today' : ''}`}>
                    {value != null ? (
                      <>
                        <span className="smx-v">{fmtSignedPct(value)}</span>
                        <span className="smx-n">{c?.frequency ?? ''}{isToday ? ' · today' : ''}</span>
                      </>
                    ) : (
                      <>
                        <span className="smx-few" title="Too little history to measure">—</span>
                        {isToday && <span className="smx-n">today</span>}
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="smx-legend">
        <span><i className="smx-sw is-well-above" />well above {baselinePct != null ? `its usual ${fmtSignedPct(baselinePct)}` : 'usual'}</span>
        <span><i className="smx-sw is-above" />above usual</span>
        <span><i className="smx-sw is-below" />below usual</span>
        <span><i className="smx-sw is-losing" />losing</span>
      </div>
      <p className="smx-foot">
        Rows: 20-day volatility. Columns: 12-week trend. Tabs: trading range. “—” = under {minN} trading days of history. How often = share of {symbol}'s measured days.
      </p>
    </div>
  );
};

export default ScreenerSetupMatrix;
